// React
import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

// Antd and MDI
import { Footer } from 'antd/lib/layout/layout'
import { Layout, Form } from 'antd'
// Components
import Pagination from '../utility/Pagination'
import SearchHeader from '../layout/SearchHeader'
import DynamicTable from '../../containers/DynamicTable'
import TableHeader from '../utility/TableHeader'

// Utils
import { SearchSegmentsEnum, PageSizes } from '../../utils/Enums'
import { Loader } from '../../utils/Functions'
import { biltyTrackingColumns } from '../../utils/TableColumns'
import moment from 'moment'
// Recoil
import { biltyTrackingSelector, dataByIDSelector } from '../../recoil/selectors'
import { useRecoilValue } from 'recoil'

// Hooks
import useFetchQuery from '../../hooks/useFetchQuery'
import useFetchLazyQuery from '../../hooks/useFetchLazyQuery'

// ERROR BOUNDARY
import { ErrorBoundary } from 'react-error-boundary'
import FallBackUI from '../errors/FallBackUI'
import { filterDataFunction, capitalizeFirstLetter } from '../../utils/Functions'
import { useOutletContext, Outlet, useParams } from 'react-router-dom'
import { LevelContext } from '../../services/LevelContext'

const { Content } = Layout

const BiltyTrackingTable = () => {
	const { id, modalType, tabStatus = 'Import' } = useParams()
	const { collection } = useOutletContext()
	const segment = useMemo(() => capitalizeFirstLetter(tabStatus), [tabStatus])

	const navigator = useNavigate()

	const [form] = Form.useForm()
	const [changedValues, setChangedValues] = useState({})
	const [orderLimits, setOrderLimits] = useState([moment().subtract(10, 'days').startOf('day'), moment().add(1, 'days').startOf('day')])
	const [isDateFiltered, setIsDateFiltered] = useState(false)
	const [loading, setLoading] = useState(false)
	const [keyword, setKeyword] = useState('')
	const [showLateBiltyOrders, setShowLateBiltyOrders] = useState(false)

	const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 10 })
	const [searchData, setSearchData] = useState({ data: [], collection: '' })
	const [filterProperty, setFilterProperty] = useState(SearchSegmentsEnum[segment]?.filterProperties?.[tabStatus]?.[0])
	const [backendPageInfo, setBackendPageInfo] = useState({ pageNo: 1, pageSize: PageSizes[pageInfo.pageSize].backend })
	const [filteredOrdersCount, setFilteredOrdersCount] = useState(0)

	const collectionData = useRecoilValue(biltyTrackingSelector({ segment, showLateBiltyOrders }))
	const modalData = useRecoilValue(dataByIDSelector({ segment, tabStatus, collection, id: id?.split('+') }))

	const advancedSearch = useFetchLazyQuery({ segment, action: 'normal', type: 'search' })

	useFetchQuery({
		segment: 'BiltyOps',
		action: 'normal',
	})

	const queryResult = useFetchQuery({
		segment,
		action: 'normal',
		backendPageInfo,
		subscription: true,
		subscriptionVariables: {
			statuses: ['Order Confirmed', 'Partial Ongoing', 'Partial Completed', 'Order Ongoing', 'Order Completed', 'Order Cancelled'],
			type: segment,
		},
		variables: { segment: segment },
	})

	useEffect(() => {
		setLoading(queryResult?.loading || advancedSearch?.loading)
	}, [collection, queryResult, advancedSearch]) // eslint-disable-line

	useEffect(() => {
		setBackendPageInfo({ pageNo: 1, pageSize: PageSizes[pageInfo.pageSize].backend })
	}, [pageInfo.pageSize]) // eslint-disable-line

	useEffect(() => {
		setBackendPageInfo({ pageNo: 1, pageSize: 20 })
	}, [segment, collection]) // eslint-disable-line

	useEffect(() => {
		setShowLateBiltyOrders(false)
		setPageInfo({ pageNo: 1, pageSize: 10 })
	}, [segment, tabStatus]) // eslint-disable-line

	useEffect(() => {
		setPageInfo((prev) => ({ ...prev, pageNo: 1 }))
	}, [keyword]) // eslint-disable-line

	useEffect(() => {
		const FilteredData = filterDataFunction({
			keyword,
			segment,
			tabStatus,
			filterProperty,
			FilteredOrders: collectionData,
		})
		setFilteredOrdersCount(FilteredData?.length)
		setSearchData({
			data: isDateFiltered
				? FilteredData
				: FilteredData?.slice(pageInfo.pageNo * pageInfo.pageSize - pageInfo.pageSize, pageInfo.pageNo * pageInfo.pageSize),
			collection: collection,
		})
	}, [collectionData, keyword, filterProperty, segment, tabStatus, collection, pageInfo, isDateFiltered])

	return (
		<>
			<Layout className='!bg-secondaryBackground'>
				<SearchHeader
					segment={segment}
					keyword={keyword}
					tabStatus={tabStatus}
					collection={collection}
					setKeyword={setKeyword}
					orderLimits={orderLimits}
					setShowLateBiltyOrders={setShowLateBiltyOrders}
					setIsDateFiltered={setIsDateFiltered}
					setOrderLimits={setOrderLimits}
					collectionData={collectionData}
					filterProperty={filterProperty}
					advancedSearch={advancedSearch}
					setFilterProperty={setFilterProperty}
				/>
				{loading ? (
					<Loader />
				) : (
					<Layout className='!bg-white rounded-lg overflow-y-hidden shadow-[0_1px_8px_rgba(0,0,0,0.16)]'>
						<Content>
							<TableHeader segment={segment} filteredCount={filteredOrdersCount} totalCount={collectionData?.length} tabStatus={tabStatus} />
							<ErrorBoundary
								fallbackRender={({ error, resetErrorBoundary }) => <FallBackUI component={'DynamicTable'} error={error} resetErrorBoundary={resetErrorBoundary} />}
								resetKeys={[searchData, tabStatus]}
							>
								<Form form={form}>
									<DynamicTable
										segment={segment}
										tabStatus={tabStatus}
										searchData={searchData}
										collection={collection}
										rowClassName='!h-28'
										tableHeight='calc(100vh - 410px)'
										columns={biltyTrackingColumns}
										columnProps={{ segment, tabStatus, collection }}
										onRow={(record) => {
											return {
												onClick: (e) => {
													navigator(`/biltyTracking/${tabStatus}/job/${record?._id}/sideDrawer`)
												},
											}
										}}
									/>
								</Form>
							</ErrorBoundary>
						</Content>
						<Footer className='flex justify-center items-center !h-16 !bg-white !py-2 !border-t-[0.5px] !border-[rgba(0, 0, 0, 0.12)]'>
							<Pagination setPageInfo={setPageInfo} setBackendPageInfo={setBackendPageInfo} totalCount={filteredOrdersCount} pageInfo={pageInfo} />
						</Footer>
					</Layout>
				)}
			</Layout>
			<LevelContext.Provider
				value={{
					loading,
					modalType,
					setChangedValues,
					props: {
						segment,
						changedValues,
						collection,
						data: {
							record: modalData?.[0],
						},
					},
				}}
			>
				<Outlet />
			</LevelContext.Provider>
		</>
	)
}

export default BiltyTrackingTable
